import React from 'react';
import { Link } from 'react-router-dom';
import { Code, IntegrationInstructions, Security, Rocket, AccountTree, Extension } from '@mui/icons-material';

const QuickLinksSection = () => {
    const quickLinks = [
        {
            icon: Code,
            title: 'Documentation API',
            description: 'Documentation technique complète avec exemples de code et références',
            link: '/documentation',
            stats: '50+ endpoints',
            gradient: 'from-amber-500 to-yellow-500'
        },
        {
            icon: IntegrationInstructions,
            title: 'Guide d\'Intégration',
            description: 'Pas-à-pas détaillé pour intégrer Netikash en 15 minutes',
            link: '/integration',
            stats: '5 étapes simples',
            gradient: 'from-orange-500 to-amber-500'
        },
        {
            icon: Security,
            title: 'Sécurité & Conformité',
            description: 'Best practices, audits de sécurité et certifications',
            link: '/security',
            stats: 'PCI DSS Level 1',
            gradient: 'from-yellow-600 to-orange-600'
        },
        {
            icon: Rocket,
            title: 'Démarrage Rapide',
            description: 'Configurez votre premier paiement en moins de 5 minutes',
            link: '/quickstart',
            stats: '5 min chrono',
            gradient: 'from-amber-400 to-yellow-400'
        },
        {
            icon: AccountTree,
            title: 'SDK & Libraries',
            description: 'Bibliothèques officielles pour tous les langages populaires',
            link: '/sdk',
            stats: '8 langages',
            gradient: 'from-orange-400 to-amber-400'
        },
        {
            icon: Extension,
            title: 'Plugins (CMS)',
            description: 'Téléchargez les plugins WordPress, WooCommerce et PrestaShop',
            link: '/plugins',
            stats: '3 plugins',
            gradient: 'from-amber-600 to-orange-600'
        }
    ];

    return (
        <section className="py-20 bg-gradient-to-br from-amber-50/80 via-orange-50/60 to-yellow-50/80 backdrop-blur-lg relative overflow-hidden dark:from-slate-900/90 dark:via-slate-900 dark:to-slate-950 transition-colors">
            {/* Background Elements */}
            <div className="absolute top-0 left-0 w-72 h-72 bg-amber-200/20 rounded-full blur-3xl -translate-x-1/2 -translate-y-1/2"></div>
            <div className="absolute bottom-0 right-0 w-96 h-96 bg-yellow-200/30 rounded-full blur-3xl translate-x-1/2 translate-y-1/2"></div>

            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
                {/* Section Header */}
                <div className="text-center mb-16">
                    <div className="inline-flex items-center px-4 py-2 rounded-full bg-amber-100 border border-amber-200 mb-4">
                        <span className="w-2 h-2 bg-amber-500 rounded-full mr-2 animate-pulse"></span>
                        <span className="text-amber-800 font-medium text-sm">RESSOURCES DÉVELOPPEUR</span>
                    </div>
                    <h2 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-4">
                        Tout pour votre{' '}
                        <span className="bg-gradient-to-r from-amber-600 to-orange-600 bg-clip-text text-transparent">
                            intégration
                        </span>
                    </h2>
                    <p className="text-xl text-gray-700 max-w-3xl mx-auto leading-relaxed">
                        Accédez à toutes nos ressources techniques, guides pratiques et outils
                        pour intégrer Netikash rapidement et efficacement.
                    </p>
                </div>

                {/* Quick Links Grid */}
                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
                    {quickLinks.map((link, index) => (
                        <div
                            key={index}
                            className="group relative bg-white/80 backdrop-blur-md rounded-2xl p-8 border border-amber-100/50 shadow-lg hover:shadow-2xl transition-all duration-500 hover:-translate-y-2"
                        >
                            {/* Hover Gradient Overlay */}
                            <div className="absolute inset-0 bg-gradient-to-br from-amber-500/5 to-yellow-500/5 rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>

                            {/* Icon Container */}
                            <div className="relative z-10">
                                <div className={`bg-gradient-to-r ${link.gradient} w-16 h-16 rounded-2xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300 shadow-lg`}>
                                    <link.icon className="text-2xl text-white" />
                                </div>

                                {/* Stats Badge */}
                                <div className="inline-flex items-center px-3 py-1 rounded-full bg-amber-100 border border-amber-200 mb-4">
                                    <span className="text-amber-700 font-medium text-xs">
                                        {link.stats}
                                    </span>
                                </div>

                                <h3 className="text-xl font-bold text-gray-900 mb-3 group-hover:text-amber-700 transition-colors">
                                    {link.title}
                                </h3>

                                <p className="text-gray-700 leading-relaxed mb-6">
                                    {link.description}
                                </p>

                                <Link
                                    to={link.link}
                                    className="inline-flex items-center space-x-2 text-amber-700 font-semibold group-hover:text-amber-800 transition-all duration-300 hover:space-x-3"
                                >
                                    <span>Découvrir</span>
                                    <svg className="w-4 h-4 group-hover:translate-x-1 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
                                    </svg>
                                </Link>
                            </div>

                            {/* Corner Accent */}
                            <div className="absolute top-0 right-0 w-16 h-16 overflow-hidden">
                                <div className="absolute top-0 right-0 w-8 h-8 bg-gradient-to-bl from-amber-500/20 to-transparent rounded-bl-2xl"></div>
                            </div>
                        </div>
                    ))}
                </div>

                {/* Bottom CTA */}
                <div className="text-center mt-12">
                    <div className="inline-flex flex-col sm:flex-row gap-4 items-center bg-white/60 backdrop-blur-md rounded-2xl p-6 border border-amber-200/50 shadow-lg">
                        <div className="text-left">
                            <h4 className="font-bold text-gray-900 text-lg">Prêt à commencer ?</h4>
                            <p className="text-gray-700">Créez votre compte de test gratuitement</p>
                        </div>
                        <a
                            href="https://gateway.netikash.com"
                            target="_blank"
                            rel="noreferrer"
                            className="bg-gradient-to-r from-amber-500 to-yellow-600 text-white px-8 py-3 rounded-lg font-semibold hover:from-amber-600 hover:to-yellow-700 transition-all transform hover:-translate-y-0.5 shadow-lg hover:shadow-xl whitespace-nowrap"
                        >
                            Créer un compte test
                        </a>
                    </div>
                </div>
            </div>
        </section>
    );
};

export default QuickLinksSection;